// The typed reminder: "Remind every [2] [days] · up to [3] times".
//
// Nate Swartz, 2026-09-29 (docs/design/documents-tab-workflow.md rule 5):
// too many picker options, "let him type it", and as short as one hour.
// Pure so the node pool proves the arithmetic; the server re-validates
// and clamps the same numbers (shared/db/esign-send.ts
// normalizeSendSchedule), so these limits are a courtesy, not the guard.

export type ReminderUnit = "hours" | "days" | "weeks";

const UNIT_MINUTES: Record<ReminderUnit, number> = { hours: 60, days: 1440, weeks: 10080 };

/** The server's ceiling (ESIGN_MAX_REMINDERS). */
export const ESIGN_UI_MAX_REMINDERS = 5;
/** One hour: the reminder job ticks hourly. */
const MIN_MINUTES = 60;
/** Thirty days: past that it is a fresh ask, not a reminder. */
const MAX_MINUTES = 30 * 1440;

/**
 * The cadence in minutes, or null for "don't remind". Null when the
 * number isn't a positive whole number or "times" is 0; clamped into
 * one hour to thirty days otherwise.
 */
export function reminderMinutes(
  amount: string,
  unit: ReminderUnit,
  timesText: string,
): number | null {
  const times = Math.floor(Number(timesText));
  if (!Number.isFinite(times) || times <= 0) return null;
  const n = Math.floor(Number(amount));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, n * UNIT_MINUTES[unit]));
}

/** "every hour", "every 4 hours", "every 2 days", "weekly", "every 2 weeks". */
export function describeCadence(minutes: number): string {
  if (minutes % 10080 === 0) {
    const w = minutes / 10080;
    return w === 1 ? "weekly" : `every ${w} weeks`;
  }
  if (minutes % 1440 === 0) {
    const d = minutes / 1440;
    return d === 1 ? "every day" : `every ${d} days`;
  }
  const h = Math.round(minutes / 60);
  return h === 1 ? "every hour" : `every ${h} hours`;
}
